/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiText,
  EuiLink,
  useEuiTheme,
  EuiHideFor,
  EuiShowFor,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useGetCostsInstances from '@modules/billing-lib/queries/useGetCostsInstances'
import useGetUsageChartsData from '@modules/billing-lib/queries/useGetUsageChartsData'
import useGetCostsItems from '@modules/billing-lib/queries/useGetCostsItems'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'
import { useConfig } from '@modules/cui/ConfigContext'
import { useServerlessGroupBy } from '@modules/billing-lib/featureflags'
import { isTrial } from '@modules/billing-lib/billingDetails/utils'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'
import Feature from '@modules/utils/feature'

import { ViewByToBucketingStrategy } from '@/components/Billing/BillingUsage/constants'
import CostsChart from '@/components/Billing/BillingUsage/components/charts/CostsChart'
import ProductChart from '@/components/Billing/BillingUsage/components/charts/ProductChart'
import ChartFilters from '@/components/Billing/BillingUsage/components/Filters/ChartFilters'
import Settings from '@/components/Billing/BillingUsage/components/Filters/Settings'
import { isFeatureActivated } from '@/store'

import ErrorCallout from '../components/ErrorCallout'
import { Action } from '../components/Filters/filtersReducer'
import { stringifyFilters, getValidationError } from '../components/Filters/utils'
import { ProductType, GroupBy } from '../types'
import withFiltersProvider from '../components/Filters/withFiltersProvider'
import useFilters from '../components/Filters/useFilters'
import MonthToDateUsage from '../components/MonthToDateUsage'
import { costChartPanelCss } from '../styles'
import PricingInfoPanel from '../components/PricingInfoPanel'

import ConnectedClustersList from './ConnectedClustersList'
import {
  getAvailableProductTypes,
  getDateRange,
  getInstanceRanking,
  getInstancesPerProductType,
} from './utils'
import DeploymentsList from './DeploymentsList'
import ProjectsList from './ProjectsList'
import ProductsList from './ProductsList'

import type { Props } from './types'
import type { FunctionComponent } from 'react'

const BillingUsageOverviewV2: FunctionComponent<Props> = ({ organizationId, setInstanceName }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const appName = useConfig('APP_NAME')
  const { filters, dispatch } = useFilters()
  const theme = useEuiTheme()
  const billingDetails = useBillingDetails()

  const areDatesValid = !getValidationError({
    startDate: filters.startDate,
    endDate: filters.endDate,
    viewBy: filters.viewBy,
  })

  const costsInstancesQuery = useGetCostsInstances(
    {
      pathParameters: {
        organization_id: organizationId,
      },
      queryParameters: { ...getDateRange(filters), include_names: true },
    },
    {
      enabled: filters.groupBy === GroupBy.Instance && areDatesValid,
    },
  )

  const costsChartDataQuery = useGetUsageChartsData(
    {
      pathParameters: {
        organization_id: organizationId,
      },
      queryParameters: {
        ...getDateRange(filters),
        instance_type: 'all',
        bucketing_strategy: ViewByToBucketingStrategy[filters.viewBy],
      },
    },
    {
      enabled: filters.productTypes.length > 0 && areDatesValid,
    },
  )

  const costsItemsV2Query = useGetCostsItems(
    {
      pathParameters: { organization_id: organizationId },
      queryParameters: {
        ...getDateRange(filters),
        serverless_group_by: useServerlessGroupBy(),
      },
    },
    {
      enabled: filters.groupBy === GroupBy.Product && areDatesValid,
    },
  )

  const instances = costsInstancesQuery.data?.instances || []
  const { deployments, projects, connectedClusters } = getInstancesPerProductType({
    instances,
    selectedSolutions: filters.solutions,
    selectedProductTypes: filters.productTypes,
  })
  const availableProductTypes = getAvailableProductTypes({
    deployments,
    projects,
    connectedClusters,
  })
  const instanceRanking = getInstanceRanking({
    deployments,
    projects,
    connectedClusters,
  })

  // Each time the filters change we want to update queryParams
  useEffect(() => {
    colorForInstances.reset()
    const queryString = stringifyFilters(filters)
    navigate(`${location.pathname}?${queryString}`, { replace: true })
  }, [filters])

  const onInstanceSelected = (instanceId: string, instanceName?: string) => {
    setInstanceName(instanceName)
    dispatch({ type: Action.SET_INSTANCE_ID, payload: { instanceId } })
  }

  const renderInstanceLists = () => {
    if (costsInstancesQuery.isError) {
      return <ErrorCallout retry={costsInstancesQuery.refetch} data-test-id='usage-table-error' />
    }

    const shouldShowDeployments = filters.productTypes.includes(ProductType.Deployments)
    const shouldShowProjects =
      filters.productTypes.includes(ProductType.Projects) && isFeatureActivated(Feature.serverless)
    const shouldShowConnectedClusters =
      filters.productTypes.includes(ProductType.CCM) && isCCMFeatureActivated()

    return (
      <EuiFlexGroup direction='column' gutterSize='xl'>
        {shouldShowProjects && (
          <EuiFlexItem>
            <ProjectsList
              isAggregated={availableProductTypes.length > 1}
              instances={projects}
              isLoading={costsInstancesQuery.isLoading}
              onSelect={onInstanceSelected}
              organizationId={organizationId}
            />
          </EuiFlexItem>
        )}

        {shouldShowDeployments && (
          <EuiFlexItem>
            <DeploymentsList
              isAggregated={availableProductTypes.length > 1}
              instances={deployments}
              isLoading={costsInstancesQuery.isLoading}
              onSelect={onInstanceSelected}
              organizationId={organizationId}
            />
          </EuiFlexItem>
        )}

        {shouldShowConnectedClusters && (
          <EuiFlexItem>
            <ConnectedClustersList
              isAggregated={availableProductTypes.length > 1}
              instances={connectedClusters}
              isLoading={costsInstancesQuery.isLoading}
              onSelect={onInstanceSelected}
              organizationId={organizationId}
            />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    )
  }

  const renderProductList = () => {
    if (costsItemsV2Query.isError) {
      return <ErrorCallout retry={costsItemsV2Query.refetch} data-test-id='usage-table-error' />
    }

    const products = costsItemsV2Query.data?.products || []

    return <ProductsList products={products} isLoading={costsItemsV2Query.isLoading} />
  }

  const renderChart = () => {
    if (costsChartDataQuery.isError) {
      return <ErrorCallout data-test-id='costs-chart-error' retry={costsChartDataQuery.refetch} />
    }

    const isProductTypeSelected = !!filters.productTypes.length
    const chartData = isProductTypeSelected ? costsChartDataQuery.data?.data : []
    const shouldShowServerlessDisclaimer =
      filters.groupBy === GroupBy.Product && isFeatureActivated(Feature.serverless)
    const chartComponent = {
      [GroupBy.Product]: (
        <ProductChart data={chartData || []} isLoading={costsChartDataQuery.isLoading} />
      ),
      [GroupBy.Instance]: (
        <CostsChart
          data={chartData || []}
          instanceRanking={instanceRanking}
          instanceTypes={availableProductTypes}
          isLoading={costsChartDataQuery.isLoading || costsInstancesQuery.isLoading}
        />
      ),
    }

    return (
      <Fragment>
        <EuiPanel
          hasShadow={false}
          paddingSize='m'
          hasBorder={true}
          data-test-id='costs-chart-panel'
          css={costChartPanelCss(theme)}
        >
          {chartComponent[filters.groupBy]}
        </EuiPanel>

        {shouldShowServerlessDisclaimer && (
          <Fragment>
            <EuiSpacer size='s' />

            <EuiText size='s'>
              <FormattedMessage
                id='usage.overview.addons-support.disclaimer'
                defaultMessage='Serverless Add-Ons and Support are included in the serverless product totals.'
              />
            </EuiText>
          </Fragment>
        )}
      </Fragment>
    )
  }

  return (
    <EuiFlexGroup data-test-id='billing-usage' gutterSize='l' alignItems='flexStart'>
      <EuiFlexItem grow={10}>
        {/* Summary section displayed for trials only */}
        {billingDetails.data && isTrial(billingDetails.data) && (
          <Fragment>
            <MonthToDateUsage organizationId={organizationId} />
            <EuiSpacer />
          </Fragment>
        )}

        {/* Mobile Filters - Flyout mode */}
        <EuiShowFor sizes={['xs', 's', 'm']}>
          <Settings isLoading={costsInstancesQuery.isLoading} flyoutMode={true} />
          <EuiSpacer size='m' />
        </EuiShowFor>

        <ChartFilters isLoading={costsInstancesQuery.isLoading} />

        <EuiSpacer size='m' />

        {renderChart()}

        <EuiSpacer size='l' />

        {
          {
            [GroupBy.Instance]: renderInstanceLists(),
            [GroupBy.Product]: renderProductList(),
          }[filters.groupBy]
        }

        <EuiSpacer size='s' />

        {filters.productTypes.length > 0 && (
          <EuiText size='s'>
            <FormattedMessage
              id='billing-usage.bill.disclaimer'
              defaultMessage='This is not your final bill, as it does not include credits, prepaids, promotional offers, or any other discounts. Check your <history>invoice</history> for the final bill.'
              values={{
                history: (content) => (
                  <EuiLink
                    onClick={() => {
                      const page = appName === 'adminconsole' ? 'billing-history' : 'history'
                      navigate(`../${page}`)
                    }}
                  >
                    {content}
                  </EuiLink>
                ),
              }}
            />
          </EuiText>
        )}
        <EuiShowFor sizes={['xs', 's', 'm']}>
          <EuiSpacer size='m' />
          <PricingInfoPanel />
        </EuiShowFor>
      </EuiFlexItem>
      <EuiFlexItem grow={3}>
        <EuiHideFor sizes={['xs', 's', 'm']}>
          <Settings isLoading={costsInstancesQuery.isLoading} />
        </EuiHideFor>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default withFiltersProvider<Props>(BillingUsageOverviewV2)
